<template>
	<v-dialog v-model="showDialog" max-width="800" persistent>
		<v-card>
			<v-card-title> Grid Fields </v-card-title>
			<v-card-text>
				<v-text-field v-model="id"
							  ref="Id"
							  :rules="rules.required"
							  class="d-none"
							  hide-details="auto"
							  label="Id" />
				<v-row>
					<v-col cols="7">
						<v-text-field v-model="label"
									  ref="Label"
									  :rules="rules.required"
									  hide-details="auto"
									  label="Label"
									  @keydown.enter="onSave" />
					</v-col>
					<v-col cols="2">
						<v-switch v-model="isVisible" label="Visible" />
					</v-col>
					<v-col cols="3">
						<v-switch v-model="isVisibleEditForm"
								  label="Visible in edit" />
					</v-col>
				</v-row>
				<v-row>
					<v-col>
						<v-btn-toggle v-model="dataFieldType"
									  class="d-flex"
									  color="primary"
									  group
									  mandatory>
							<v-btn :value="1" class="flex-grow-1">
								<v-icon>mdi-table</v-icon>Table Field
							</v-btn>
							<v-btn :value="2" class="flex-grow-1">
								<v-icon>mdi-link-variant</v-icon>Link Field
							</v-btn>
							<v-btn :value="3" class="flex-grow-1">
								<v-icon>mdi-form-dropdown</v-icon>Reference
								Field
							</v-btn>
						</v-btn-toggle>
					</v-col>
				</v-row>
				<v-row v-if="isReferenceField">
					<v-col>
						<v-autocomplete v-if="isReferenceField"
										v-model="referenceTypeId"
										ref="ReferenceTypeId"
										:items="referenceTypes"
										:rules="rules.required"
										item-text="Name"
										item-value="Id"
										label="Reference List" />
					</v-col>
				</v-row>
				<v-row>
					<v-col>
						<v-autocomplete v-model="dataFieldId"
										ref="DataFieldId"
										:allow-overflow="false"
										:items="availableDataFields"
										:rules="rules.required"
										hide-selected
										item-text="FieldName"
										:filter="customFilter"
										item-value="Id"
										:label="isReferenceField ? 'Link Field' : 'Data Field'">

							<template v-slot:selection="data">
								{{ data.item.EntityName }} -
								{{ data.item.FieldName	}}
								<span v-if="data.item.GvfLabel">
									&nbsp; ({{ data.item.GvfLabel }})
								</span>
							</template>
							<template v-slot:item="data">
								<template v-if="isObject(data.item)">
									<v-list-item-content>
										{{
										data.header
										}}
									</v-list-item-content>
								</template>
								<template v-else>
									<v-list-item-content>
										<v-list-item-title>
											{{ data.item.FieldName }}
											<span v-if="data.item.GvfLabel">
												({{ data.item.GvfLabel }})
											</span>
										</v-list-item-title>
										<v-list-item-subtitle>
											{{
											data.item.EntityName
											}}
										</v-list-item-subtitle>
									</v-list-item-content>
								</template>
							</template>
						</v-autocomplete>
					</v-col>
				</v-row>
				<v-row v-if="isLinkField">
					<v-col>
						<v-autocomplete v-if="isLinkField"
										v-model="linkFieldId"
										ref="LinkFieldId"
										:items="linkFields"
										:rules="rules.required"
										item-text="Label"
										item-value="Id"
										label="Link Field"
										:filter="customFilter">
							<template v-slot:selection="data">
								{{ data.item.EntityName }} -
								{{ data.item.FieldName }}
							</template>
							<template v-slot:item="data">
								<template v-if="isObject(data.item)">
									<v-list-item-content>
										{{
										data.header
										}}
									</v-list-item-content>
								</template>
								<template v-else>
									<v-list-item-content>
										<v-list-item-title>
											{{
											data.item.FieldName
											}}
										</v-list-item-title>
										<v-list-item-subtitle>
											{{
											data.item.EntityName
											}}
										</v-list-item-subtitle>
									</v-list-item-content>
								</template>
							</template>
						</v-autocomplete>
					</v-col>
				</v-row>
			</v-card-text>
			<v-divider />
			<v-card-actions>
				<v-spacer />
				<v-btn @click.stop="onClose">Close</v-btn>
				<v-btn color="primary" @click.stop="onSave">
					<v-icon class="mr-2">mdi-content-save</v-icon>
					Save
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script>
	import { mapGetters } from 'vuex';
	import {
		GridFieldsSettingsGetters,
		GridsGetters,
	} from '../../../store/getters';
	import { FieldDataTypes } from '../../../enums/fieldDataTypes';
	import { GridFieldsSettingsActions } from '../../../store/actions';

	export default {
		name: 'GridFieldsModal',
		props: {
			showDialog: Boolean,
		},
		data() {
			return {
				dataFieldType: 1,
				id: null,
				label: '',
				isVisible: true,
				isVisibleEditForm: true,
				dataFieldId: null,
				linkFieldId: null,
				referenceTypeId: null,
				order: null,
				rules: {
					required: [(value) => !!value || 'Field is required'],
				},
			};
		},
		computed: {
			...mapGetters({
				entity: [GridFieldsSettingsGetters.GET_SELECTED_ENTITY],
				entities: [GridsGetters.GET_ENTITIES],
				currentGridViewFields: [
					GridFieldsSettingsGetters.GET_SELECTED_GRIDVIEW_FIELDS,
				],
				dataFields: [GridsGetters.GET_DATA_FIELDS],
				referenceTypes: [GridsGetters.GET_REFERENCE_LIST_TYPES],
				selectedGridview: [GridFieldsSettingsGetters.GET_SELECTED_GRIDVIEW],
				editField: [GridFieldsSettingsGetters.GET_EDIT_FIELD],
				otherEntitiesGridViewFields: [
					GridsGetters.GET_LINK_FIELD_DATA_FIELDS,
				],
			}),

			availableDataFields() {
				switch (this.dataFieldType) {
					case 2: {
						let result = [];
						let usedDataFieldsIds =
							this.currentGridViewFields &&
								this.currentGridViewFields.length > 0
								? this.currentGridViewFields.map(
									(x) => x.DataFieldId
								)
								: [];
						if (this.editField && this.editField.DataFieldId) {
							let index = usedDataFieldsIds.indexOf(
								this.editField.DataFieldId
							);
							if (index > -1) {
								usedDataFieldsIds.splice(index, 1);
							}
						}
						result = this.otherEntitiesGridViewFields
							.filter(
								(x) =>
									usedDataFieldsIds.indexOf(x.DataField.Id) == -1
							)
							.map((x) => ({ GvfLabel: x.GvfLabel, ...x.DataField }))
							.sort((a, b) => (a.FieldName > b.FieldName ? 1 : -1));

						return result;
					}
					case 3:
						return this.dataFields
							.filter(
								(x) =>
									x.DataType ===
									FieldDataTypes.UNIQUE_IDENTIFIER &&
									x.EntityId === this.entity.Id
							)
							.sort((a, b) => (a.FieldName > b.FieldName ? 1 : -1));
					default: {
						let usedDataFieldsIds =
							this.currentGridViewFields &&
								this.currentGridViewFields.length > 0
								? this.currentGridViewFields.map(
									(x) => x.DataFieldId
								)
								: [];
						if (this.editField && this.editField.DataFieldId) {
							let index = usedDataFieldsIds.indexOf(
								this.editField.DataFieldId
							);
							if (index > -1) {
								usedDataFieldsIds.splice(index, 1);
							}
						}
						return this.dataFields
							.filter((x) => {
								return (
									(x.EntityId === this.entity.Id &&
										usedDataFieldsIds.indexOf(x.Id) === -1) ||
									x.Id === this.editField.Id
								);
							})
							.sort((a, b) => (a.FieldName > b.FieldName ? 1 : -1));
					}
				}
			},

			linkFields() {
				if (this.dataFieldType === 2) {
					return this.dataFields
						.filter(
							(x) =>
								x.DataType === FieldDataTypes.UNIQUE_IDENTIFIER &&
								x.EntityId === this.entity.Id
						)
						.sort((a, b) => (a.FieldName > b.FieldName ? 1 : -1));
				}
				return [];
			},
			isLinkField() {
				return this.dataFieldType === 2;
			},
			isReferenceField() {
				return this.dataFieldType === 3;
			},
		},
		mounted() {
			if (this.editField.Id) {
				this.dataFieldType = this.editField.ReferenceListTypeId
					? 3
					: this.editField.LinkFieldId
						? 2
						: 1;
				this.id = this.editField.Id;
				this.label = this.editField.Label;
				this.isVisible = this.editField.IsVisible;
				this.isVisibleEditForm = this.editField.IsVisibleEditForm;
				this.dataFieldId = this.editField.DataFieldId;
				this.linkFieldId = this.editField.LinkFieldId;
				this.referenceTypeId = this.editField.ReferenceListTypeId;
				this.order = this.editField.Order;
			}
		},
		watch: {
			dataFieldType() {
				if (!this.editField.Id) {
					this.dataFieldId = null;
					this.linkFieldId = null;
					this.referenceTypeId = null;
				}
			},
		},
		methods: {
			customFilter(item, queryText) {
				if (item.FieldName)
					if (
						item.FieldName.toLowerCase().includes(
							queryText.toLowerCase()
						)
					)
						return item;
				if (item.GvfLabel)
					if (
						item.GvfLabel.toLowerCase().includes(
							queryText.toLowerCase()
						)
					)
						return item;
			},
			validate() {
				let formHasErrors = false;
				let formFields = {
					Label: this.label,
					DataFieldId: this.dataFieldId,
				};

				if (this.dataFieldType === 2) {
					formFields.LinkFieldId = this.linkFieldId;
				}
				if (this.dataFieldType === 3) {
					formFields.ReferenceTypeId = this.referenceTypeId;
				}

				Object.keys(formFields).forEach((index) => {
					if (!formFields[index]) formHasErrors = true;
					this.$refs[index].validate(true);
				});
				return !formHasErrors;
			},
			onClose() {
				this.$emit('close');
			},
			onSave() {
				if (!this.validate()) return;

				let newField = {
					Id: this.id,
					GridViewId: this.selectedGridview.Id,
					Order: this.order || this.currentGridViewFields.length + 1,
					Label: this.label,
					DataFieldId: this.dataFieldId,
					LinkFieldId: this.dataFieldType === 2 ? this.linkFieldId : null,
					IsVisible: this.isVisible,
					IsVisibleEditForm: this.isVisibleEditForm,
					ReferenceListTypeId:
						this.dataFieldType === 3 ? this.referenceTypeId : null,
				};
				if (this.id !== null) {
					this.$store
						.dispatch(GridFieldsSettingsActions.UPDATE_FIELD, newField)
						.then((resp) => {
							if (!resp) return;
							this.$emit('close');
						});
				} else {
					delete newField.Id;
					this.$store
						.dispatch(GridFieldsSettingsActions.ADD_FIELD, newField)
						.then((resp) => {
							if (!resp) return;
							this.$emit('close');
						});
				}
			},
			isObject(item) {
				return typeof item !== 'object';
			},
		},
	};
</script>

<style scoped>
	.row + .row {
		margin-top: 0;
	}

	.v-input--selection-controls {
		margin-top: 0;
		padding-top: 0;
	}

	.v-text-field {
		margin-top: 0;
		padding-top: 0;
	}
</style>
